import * as Blockly from 'blockly';

// Constants
const PREFIX = 'fvSolution';
const BLOCK_COLOR = 200; // Base color for fvSolution blocks

// Consistent color scheme
const COLORS = {
  BASE: BLOCK_COLOR,              // 200 - Root blocks (dict, foamfile)
  SOLVER: BLOCK_COLOR + 10,       // 210 - Solver blocks
  ALGORITHM: BLOCK_COLOR + 20,    // 220 - Algorithm blocks 
  RELAXATION: BLOCK_COLOR + 30,   // 230 - Relaxation factor blocks
  VALUE: BLOCK_COLOR + 5,         // 205 - Value blocks
  DROPDOWN: BLOCK_COLOR + 15      // 215 - Dropdown blocks
};

// Default FoamFile entries for fvSolution
const DEFAULT_FOAMFILE_ENTRIES = {
  version: '2.0',
  format: 'ascii',
  class: 'dictionary',
  location: 'system',
  object: 'fvSolution'
};



// Basic solver options
const SOLVER_OPTIONS = [
  ['GAMG', 'GAMG'],
  ['smoothSolver', 'smoothSolver'],
  ['PCG', 'PCG'],
  ['PBiCGStab', 'PBiCGStab'],
  ['diagonal', 'diagonal']
];

// Basic smoother options  
const SMOOTHER_OPTIONS = [
  ['GaussSeidel', 'GaussSeidel'],
  ['symGaussSeidel', 'symGaussSeidel'],
  ['DIC', 'DIC'],
  ['DILU', 'DILU'],
  ['DICGaussSeidel', 'DICGaussSeidel']
];

// Define solver parameter configurations following fvSchemes pattern
const SOLVER_CONFIGS = {
  // Solver parameter configurations
  solverParameters: [
    {
      key: 'solver',
      shadow: `${PREFIX}_solver_type_dropdown`,
      check: 'String',
      tooltip: 'Linear solver type',
      validator: (value) => {
        const validSolvers = SOLVER_OPTIONS.map(opt => opt[1]);
        return validSolvers.includes(value) ? value : 'GAMG';
      },
      defaultValue: 'GAMG',
      icon: '🔧',
      color: COLORS.SOLVER
    },
    {
      key: 'smoother',
      shadow: `${PREFIX}_smoother_dropdown`,
      check: 'String',
      tooltip: 'Smoother/preconditioner type',
      validator: (value) => {
        const validSmoothers = SMOOTHER_OPTIONS.map(opt => opt[1]);
        return validSmoothers.includes(value) ? value : 'GaussSeidel';
      },
      defaultValue: 'GaussSeidel',
      icon: '🔧',
      color: COLORS.SOLVER
    },
    {
      key: 'tolerance',
      shadow: `${PREFIX}_value_scientific`,
      check: 'Number',
      tooltip: 'Solver tolerance',
      validator: (value) => {
        const num = Number(value);
        return !isNaN(num) && num > 0 ? num : 1e-6;
      },
      defaultValue: 1e-6,
      icon: '📏',
      color: COLORS.VALUE
    },
    {
      key: 'relTol',
      shadow: `${PREFIX}_value_number`,
      check: 'Number',
      tooltip: 'Relative tolerance',
      validator: (value) => {
        const num = Number(value);
        return !isNaN(num) && num >= 0 && num <= 1 ? num : 0.1;
      },
      defaultValue: 0.1,
      icon: '📏',
      color: COLORS.VALUE
    },
    {
      key: 'nCellsInCoarsestLevel',
      shadow: `${PREFIX}_value_number`,
      check: 'Number',
      tooltip: 'Number of cells in coarsest level for multigrid',
      validator: (value) => {
        const num = parseInt(value, 10);
        return !isNaN(num) && num > 0 ? num : 10;
      },
      defaultValue: 10,
      icon: '🔢',
      color: COLORS.VALUE
    }
  ]
};

/**
 * Utility to create and initialize a Blockly block
 */
function createBlock(workspace, blockType) {
  try {
    if (!Blockly.Blocks[blockType]) {
      throw new Error(`Block type ${blockType} not defined`);
    }
    const block = workspace.newBlock(blockType);
    block.initSvg();
    block.render();
    return block;
  } catch (error) {
    console.error(`Error creating block ${blockType}:`, error);
    return null;
  }
}

/**
 * Utility to connect a value block to a block's input
 */
function connectValueBlock(block, valueBlock, entryKey) {
  try {
    const valueInput = block.getInput('VALUE') || block.getInput(entryKey);
    if (!valueInput) {
      console.warn(`No VALUE or ${entryKey} input found for block with key ${entryKey}`);
      return false;
    }

    if (!valueBlock || !valueBlock.outputConnection) {
      console.warn(`No valid output connection for value block of ${entryKey}`);
      return false;
    }

    const originalCheck = valueInput.connection.getCheck();
    valueInput.connection.setCheck(null);
    valueInput.connection.connect(valueBlock.outputConnection);
    valueInput.connection.setCheck(originalCheck);
    console.log(`Connected value block for ${entryKey}`);
    return true;
  } catch (error) {
    console.error(`Failed to connect value block for ${entryKey}:`, error);
    return false;
  }
}

// Register all blocks for this dictionary
export function registerBlocks(params = {}) {
  try {
    const filename = params.filename || 'fvSolution';
    
    console.log(`Registering fvSolution-specific blocks for ${filename}`);
    registerRootBlock(filename);
    registerSolverBlocks(filename);
    registerAlgorithmBlocks(filename);
    registerSpecializedKeywordBlocks(filename);
    registerRelaxationBlocks(filename);
    registerFoamFileBlock(filename);
    registerValueBlocks(filename);
    registerDropdownBlocks(filename);
    console.log(`Registered ${filename} blocks:`, Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)));
  } catch (error) {
    console.error(`Error registering blocks for ${params.filename || 'fvSolution'}:`, error);
  }
}

// Register the root block
function registerRootBlock(filename) {
  const id = `${PREFIX}_dict`;
  if (Blockly.Blocks[id]) {
    console.log(`Root block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;
      this.jsonInit({
        type: id,
        message0: '%1 %2',
        args0: [
          {
            type: 'field_label',
            name: 'DICT_NAME',
            text: filename
          },
          {
            type: 'field_label',
            name: 'FILENAME',
            text: `(${filename})`
          }
        ],
        inputsInline: true,
        nextStatement: null,
        colour: COLORS.BASE,
        tooltip: `Root dictionary for ${filename}`,
        helpUrl: ''
      });
      
      this.appendStatementInput('ENTRIES')
        .setCheck(null)
        .appendField('entries');
    }
  };
  
  console.log(`Registered root block: ${id}`);
}

// Register solver blocks
function registerSolverBlocks(filename) {
  // Solvers section block
  const solversId = `${PREFIX}_solvers_section`;
  if (!Blockly.Blocks[solversId]) {
    Blockly.Blocks[solversId] = {
      init: function() {
        this.jsonInit({
          type: solversId,
          message0: '⚙️ solvers',
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.SOLVER,
          tooltip: 'Solver configurations section',
          helpUrl: ''
        });
        
        this.appendStatementInput('SOLVERS')
          .setCheck(null)
          .appendField('configurations');
      }
    };
  }

  // Generic solver configuration block
  const solverConfigId = `${PREFIX}_solver_config`;
  if (!Blockly.Blocks[solverConfigId]) {
    Blockly.Blocks[solverConfigId] = {
      init: function() {
        this.jsonInit({
          type: solverConfigId,
          message0: '🔧 %1 solver',
          args0: [
            {
              type: 'field_input',
              name: 'SOLVER_NAME',
              text: 'p'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.SOLVER,
          tooltip: 'Generic solver configuration block',
          inputsInline: true
        });
        
        this.appendStatementInput('PARAMETERS')
          .setCheck(null)
          .appendField('parameters');
      }
    };
  }
}

// Register algorithm blocks
function registerAlgorithmBlocks(filename) {
  // Generic algorithm block
  const algorithmId = `${PREFIX}_algorithm`;
  if (!Blockly.Blocks[algorithmId]) {
    Blockly.Blocks[algorithmId] = {
      init: function() {
        this.jsonInit({
          type: algorithmId,
          message0: '🔄 %1 algorithm',
          args0: [
            {
              type: 'field_input',
              name: 'ALGORITHM_NAME',
              text: 'SIMPLE'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.ALGORITHM,
          tooltip: 'Algorithm configuration (SIMPLE, PIMPLE, etc.)',
          helpUrl: ''
        });
        
        this.appendStatementInput('PARAMETERS')
          .setCheck(null)
          .appendField('parameters');
      }
    };
  }

  // Algorithm parameter blocks
  const nNonOrthoCorrId = `${PREFIX}_nNonOrthogonalCorrectors`;
  if (!Blockly.Blocks[nNonOrthoCorrId]) {
    Blockly.Blocks[nNonOrthoCorrId] = {
      init: function() {
        this.jsonInit({
          type: nNonOrthoCorrId,
          message0: 'nNonOrthogonalCorrectors %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'Number'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Number of non-orthogonal correctors',
          inputsInline: true
        });
      }
    };
  }

  const nCorrectorsId = `${PREFIX}_nCorrectors`;
  if (!Blockly.Blocks[nCorrectorsId]) {
    Blockly.Blocks[nCorrectorsId] = {
      init: function() {
        this.jsonInit({
          type: nCorrectorsId,
          message0: 'nCorrectors %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'Number'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Number of PIMPLE correctors',
          inputsInline: true
        });
      }
    };
  }

  // New solver parameter blocks
  const solverId = `${PREFIX}_solver`;
  if (!Blockly.Blocks[solverId]) {
    Blockly.Blocks[solverId] = {
      init: function() {
        this.jsonInit({
          type: solverId,
          message0: 'solver %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'String'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Linear solver type',
          inputsInline: true
        });
      }
    };
  }

  const smootherParamId = `${PREFIX}_smoother`;
  if (!Blockly.Blocks[smootherParamId]) {
    Blockly.Blocks[smootherParamId] = {
      init: function() {
        this.jsonInit({
          type: smootherParamId,
          message0: 'smoother %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'String'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Smoother/preconditioner type',
          inputsInline: true
        });
      }
    };
  }

  const toleranceId = `${PREFIX}_tolerance`;
  if (!Blockly.Blocks[toleranceId]) {
    Blockly.Blocks[toleranceId] = {
      init: function() {
        this.jsonInit({
          type: toleranceId,
          message0: 'tolerance %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'Number'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Solver tolerance',
          inputsInline: true
        });
      }
    };
  }

  const relTolId = `${PREFIX}_relTol`;
  if (!Blockly.Blocks[relTolId]) {
    Blockly.Blocks[relTolId] = {
      init: function() {
        this.jsonInit({
          type: relTolId,
          message0: 'relTol %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'Number'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Relative tolerance',
          inputsInline: true
        });
      }
    };
  }

  const nCellsInCoarsestLevelId = `${PREFIX}_nCellsInCoarsestLevel`;
  if (!Blockly.Blocks[nCellsInCoarsestLevelId]) {
    Blockly.Blocks[nCellsInCoarsestLevelId] = {
      init: function() {
        this.jsonInit({
          type: nCellsInCoarsestLevelId,
          message0: 'nCellsInCoarsestLevel %1',
          args0: [
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'Number'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.VALUE,
          tooltip: 'Number of cells in coarsest level for multigrid',
          inputsInline: true
        });
      }
    };
  }
}

// Register specialized keyword blocks for solver parameters following fvSchemes pattern
function registerSpecializedKeywordBlocks(filename) {
  // Create specialized blocks for each solver parameter
  Object.entries(SOLVER_CONFIGS).forEach(([sectionName, params]) => {
    params.forEach(config => {
      const safeKey = config.key.replace(/[^a-zA-Z0-9]/g, '_');
      const keywordId = `${PREFIX}_keyword_${safeKey}`;
      
      if (Blockly.Blocks[keywordId]) return;

      // Create the specialized keyword block
      Blockly.Blocks[keywordId] = {
        init: function() {
          this.filename = filename;
          
          this.jsonInit({
            type: keywordId,
            message0: '%1 %2 %3',
            args0: [
              {
                type: 'field_label',
                name: 'PARAM_ICON',
                text: config.icon
              },
              {
                type: 'field_input',
                name: 'KEY',
                text: config.key,
                validator: function(newValue) {
                  // Allow editing but default to the config key
                  return newValue || config.key;
                }
              },
              {
                type: 'input_value',
                name: 'VALUE',
                check: config.check
              }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: config.color,
            tooltip: `${config.tooltip} (${filename})`,
            inputsInline: true
          });
          
          // Set default shadow block based on config
          const input = this.getInput('VALUE');
          if (input && config.shadow) {
            const parser = new DOMParser();
            let shadowXml;
            
            // Create appropriate shadow based on the parameter type
            switch (config.key) {
              case 'solver':
                shadowXml = `
                  <shadow type="${PREFIX}_solver_type_dropdown">
                    <field name="SOLVER">${config.defaultValue}</field>
                  </shadow>
                `;
                break;
              case 'smoother':
                shadowXml = `
                  <shadow type="${PREFIX}_smoother_dropdown">
                    <field name="SMOOTHER">${config.defaultValue}</field>
                  </shadow>
                `;
                break;
              case 'tolerance':
                shadowXml = `
                  <shadow type="${PREFIX}_value_scientific">
                    <field name="MANTISSA">1</field>
                    <field name="EXPONENT">-6</field>
                  </shadow>
                `;
                break;
              case 'relTol':
              case 'nCellsInCoarsestLevel':
                shadowXml = `
                  <shadow type="${PREFIX}_value_number">
                    <field name="VALUE">${config.defaultValue}</field>
                  </shadow>
                `;
                break;
              default:
                shadowXml = `
                  <shadow type="${PREFIX}_value_string">
                    <field name="VALUE">${config.defaultValue}</field>
                  </shadow>
                `;
                break;
            }
            
            const shadowDom = parser.parseFromString(shadowXml, 'text/xml').documentElement;
            input.connection.setShadowDom(shadowDom);
          }
          
          console.log(`Created specialized keyword block ${keywordId} for ${config.key}`);
        }
      };
      
      console.log(`Registered specialized keyword block: ${keywordId} for ${filename}`);
    });
  });
}

// Register relaxation blocks
function registerRelaxationBlocks(filename) {
  // Relaxation factors section
  const relaxationId = `${PREFIX}_relaxationFactors_section`;
  if (!Blockly.Blocks[relaxationId]) {
    Blockly.Blocks[relaxationId] = {
      init: function() {
        this.jsonInit({
          type: relaxationId,
          message0: '⚖️ relaxationFactors',
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.RELAXATION,
          tooltip: 'Relaxation factors section',
          inputsInline: true
        });
        
        this.appendStatementInput('FACTORS')
          .setCheck(null)
          .appendField('factors');
      }
    };
  }

  // Individual relaxation factor block
  const relaxationFactorId = `${PREFIX}_relaxation_factor`;
  if (!Blockly.Blocks[relaxationFactorId]) {
    Blockly.Blocks[relaxationFactorId] = {
      init: function() {
        this.jsonInit({
          type: relaxationFactorId,
          message0: '%1 %2',
          args0: [
            {
              type: 'field_input',
              name: 'VARIABLE',
              text: 'U'
            },
            {
              type: 'input_value',
              name: 'VALUE',
              check: 'Number'
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.RELAXATION,
          tooltip: 'Individual relaxation factor for a variable',
          inputsInline: true
        });
      }
    };
  }
}



// Register FoamFile block
function registerFoamFileBlock(filename) {
  const id = `${PREFIX}_foamfile`;
  if (Blockly.Blocks[id]) {
    console.log(`FoamFile block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;
      this.jsonInit({
        type: id,
        message0: 'FoamFile',
        message1: 'version %1',
        args1: [
          {
            type: 'input_value',
            name: 'VERSION',
            check: 'String'
          }
        ],
        message2: 'format %1',
        args2: [
          {
            type: 'input_value',
            name: 'FORMAT',
            check: 'String'
          }
        ],
        message3: 'class %1',
        args3: [
          {
            type: 'input_value',
            name: 'CLASS',
            check: 'String'
          }
        ],
        message4: 'location %1',
        args4: [
          {
            type: 'input_value',
            name: 'LOCATION',
            check: 'String'
          }
        ],
        message5: 'object %1',
        args5: [
          {
            type: 'input_value',
            name: 'OBJECT',
            check: 'String'
          }
        ],
        previousStatement: null,
        nextStatement: null,
        colour: COLORS.BASE,
        tooltip: `FoamFile dictionary for ${filename}`,
        helpUrl: '',
      });

      // Set shadow blocks
      const fields = ['VERSION', 'FORMAT', 'CLASS', 'LOCATION', 'OBJECT'];
      const parser = new DOMParser();
      fields.forEach(field => {
        const input = this.getInput(field);
        if (input) {
          const shadowXmlString = `
            <shadow type="${PREFIX}_value_string">
              <field name="VALUE">${DEFAULT_FOAMFILE_ENTRIES[field.toLowerCase()]}</field>
            </shadow>
          `;
          const shadowXml = parser.parseFromString(shadowXmlString, 'text/xml').documentElement;
          input.connection.setShadowDom(shadowXml);
          const shadowBlock = input.connection.targetBlock();
          if (shadowBlock) {
            shadowBlock.filename = filename;
          }
        }
      });
    },
  };

  console.log(`Registered FoamFile block: ${id} for ${filename}`);
}

// Register value blocks
function registerValueBlocks(filename) {
  // String value block
  const valueStringType = `${PREFIX}_value_string`;
  if (!Blockly.Blocks[valueStringType]) {
    Blockly.Blocks[valueStringType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: valueStringType,
          message0: '❝ %1 ❞',
          args0: [
            {
              type: 'field_input',
              name: 'VALUE',
              text: ''
            }
          ],
          output: 'String',
          colour: COLORS.VALUE,
          tooltip: `String value for ${filename}`,
          editable: true
        });
      }
    };
  }

  // Number value block
  const valueNumberType = `${PREFIX}_value_number`;
  if (!Blockly.Blocks[valueNumberType]) {
    Blockly.Blocks[valueNumberType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: valueNumberType,
          message0: '# %1',
          args0: [
            {
              type: 'field_number',
              name: 'VALUE',
              value: 0,
              precision: 0.001
            }
          ],
          output: 'Number',
          colour: COLORS.VALUE,
          tooltip: `Numeric value for ${filename}`,
          editable: true
        });
      }
    };
  }

  // Scientific notation value block
  const valueScientificType = `${PREFIX}_value_scientific`;
  if (!Blockly.Blocks[valueScientificType]) {
    Blockly.Blocks[valueScientificType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: valueScientificType,
          message0: '%1e%2',
          args0: [
            {
              type: 'field_number',
              name: 'MANTISSA',
              value: 1,
              min: 1,
              max: 9.999,
              precision: 0.001
            },
            {
              type: 'field_number',
              name: 'EXPONENT',
              value: -6,
              precision: 1
            }
          ],
          output: 'Number',
          colour: COLORS.VALUE,
          tooltip: 'Scientific notation value (e.g., 1e-6)',
          inputsInline: true
        });
      }
    };
  }
}

// Register dropdown blocks
function registerDropdownBlocks(filename) {
  // Solver type dropdown
  const solverTypeId = `${PREFIX}_solver_type_dropdown`;
  if (!Blockly.Blocks[solverTypeId]) {
    Blockly.Blocks[solverTypeId] = {
      init: function() {
        this.jsonInit({
          type: solverTypeId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'SOLVER',
              options: SOLVER_OPTIONS
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Linear solver type'
        });
      }
    };
  }

  // Smoother dropdown
  const smootherId = `${PREFIX}_smoother_dropdown`;
  if (!Blockly.Blocks[smootherId]) {
    Blockly.Blocks[smootherId] = {
      init: function() {
        this.jsonInit({
          type: smootherId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'SMOOTHER',
              options: SMOOTHER_OPTIONS
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Smoother/preconditioner type'
        });
      }
    };
  }
}

// Generate toolbox XML
function generateToolbox() {
  return `
<xml>
  <!-- Root blocks -->
  <block type="${PREFIX}_dict">
    <field name="DICT_NAME">fvSolution</field>
  </block>
  
  <block type="${PREFIX}_foamfile">
    <value name="VERSION">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">2.0</field>
      </shadow>
    </value>
    <value name="FORMAT">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">ascii</field>
      </shadow>
    </value>
    <value name="CLASS">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">dictionary</field>
      </shadow>
    </value>
    <value name="LOCATION">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">system</field>
      </shadow>
    </value>
    <value name="OBJECT">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">fvSolution</field>
      </shadow>
    </value>
  </block>
  
  <!-- Section blocks -->
  <block type="${PREFIX}_solvers_section"></block>
  
  <!-- Solver blocks -->
  <block type="${PREFIX}_solver_config">
    <field name="SOLVER_NAME">p</field>
  </block>
  
  <!-- Algorithm blocks -->
  <block type="${PREFIX}_algorithm">
    <field name="ALGORITHM_NAME">SIMPLE</field>
  </block>
  
  <!-- Algorithm parameter blocks -->
  <block type="${PREFIX}_nNonOrthogonalCorrectors">
    <value name="VALUE">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">0</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_nCorrectors">
    <value name="VALUE">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">3</field>
      </shadow>
    </value>
  </block>
  
  <!-- Specialized keyword blocks -->
  <block type="${PREFIX}_keyword_solver">
    <field name="KEY">solver</field>
  </block>
  
  <block type="${PREFIX}_keyword_smoother">
    <field name="KEY">smoother</field>
  </block>
  
  <block type="${PREFIX}_keyword_tolerance">
    <field name="KEY">tolerance</field>
  </block>
  
  <block type="${PREFIX}_keyword_relTol">
    <field name="KEY">relTol</field>
  </block>
  
  <block type="${PREFIX}_keyword_nCellsInCoarsestLevel">
    <field name="KEY">nCellsInCoarsestLevel</field>
  </block>
  
  <!-- Relaxation blocks -->
  <block type="${PREFIX}_relaxationFactors_section"></block>
  
  <block type="${PREFIX}_relaxation_factor">
    <field name="VARIABLE">U</field>
    <value name="VALUE">
      <shadow type="${PREFIX}_value_number">
        <field name="VALUE">0.5</field>
      </shadow>
    </value>
  </block>
  

  
  <!-- Value blocks -->
  <block type="${PREFIX}_value_string">
    <field name="VALUE">value</field>
  </block>
  
  <block type="${PREFIX}_value_number">
    <field name="VALUE">0</field>
  </block>
  
  <block type="${PREFIX}_value_scientific">
    <field name="MANTISSA">1</field>
    <field name="EXPONENT">-6</field>
  </block>
  
  <!-- Dropdown blocks -->
  <block type="${PREFIX}_solver_type_dropdown">
    <field name="SOLVER">GAMG</field>
  </block>
  
  <block type="${PREFIX}_smoother_dropdown">
    <field name="SMOOTHER">GaussSeidel</field>
  </block>
</xml>`;
}

// Find matching block type for parsing
function findMatchingBlockType(component, dictName = 'fvSolution', path = '') {
  if (!component || typeof component !== 'object') {
    return null;
  }

  const { type, name, value, key } = component;
  const keyName = key || name;

  console.log(`Finding matching block type for component: type=${type}, key=${keyName} at path: ${path}`);

  if (type === 'dictionary') {
    if (name === 'FoamFile') {
      return `${PREFIX}_foamfile`;
    }
    if (name === 'solvers') {
      return `${PREFIX}_solvers_section`;
    }
    if (['SIMPLE', 'PIMPLE', 'potentialFlow'].includes(name)) {
      return `${PREFIX}_algorithm`;
    }
    if (name === 'relaxationFactors') {
      return `${PREFIX}_relaxationFactors_section`;
    }
    // Check if this is a solver configuration dictionary (inside solvers section)
    if (path.includes('solvers') && ['p', 'pFinal', 'Phi', 'U', 'k', 'omega', 'epsilon', 'nuTilda'].includes(name)) {
      return `${PREFIX}_solver_config`;
    }
    // Also handle regex pattern for complex solver names like "(U|k|omega)"
    if (path.includes('solvers') && name && name.match(/^\(.*\)$/)) {
      return `${PREFIX}_solver_config`;
    }
    return `${PREFIX}_dict`;
  }

  if (type === 'keyValue') {
    // Check if we're inside a solver configuration based on path structure
    // Any path that has 'solvers_<anything>_<parameter>' indicates we're inside a solver config
    const solversMatch = path.match(/.*solvers_([^_]+)_/);
    const isInSolverConfig = solversMatch !== null;
    const solverName = solversMatch ? solversMatch[1] : null;
    
    console.log(`Checking keyValue ${keyName}: isInSolverConfig=${isInSolverConfig}, solverName=${solverName}, path=${path}`);
    
    // Solver configuration blocks - only if not inside a solver config and we're directly under solvers
    const isDirectSolverConfig = path.includes('solvers') && !isInSolverConfig && 
      (path.match(/.*solvers_[^_]+$/) || keyName.match(/^\(.*\)$/));
    
    if (isDirectSolverConfig) {
      console.log(`Creating solver config block for solver: ${keyName}`);
      return `${PREFIX}_solver_config`;
    }
    
    // Algorithm parameters - flexible matching
    if (keyName === 'nNonOrthogonalCorrectors') {
      return `${PREFIX}_nNonOrthogonalCorrectors`;
    }
    if (keyName === 'nCorrectors') {
      return `${PREFIX}_nCorrectors`;
    }
    
    // Solver parameters - use specialized keyword blocks when inside any solver config
    if (isInSolverConfig && keyName === 'solver') {
      console.log(`Using specialized solver keyword block for ${keyName} in solver config '${solverName}' at path: ${path}`);
      return `${PREFIX}_keyword_solver`;
    }
    if (isInSolverConfig && keyName === 'smoother') {
      console.log(`Using specialized smoother keyword block for ${keyName} in solver config '${solverName}' at path: ${path}`);
      return `${PREFIX}_keyword_smoother`;
    }
    if (isInSolverConfig && keyName === 'tolerance') {
      console.log(`Using specialized tolerance keyword block for ${keyName} in solver config '${solverName}' at path: ${path}`);
      return `${PREFIX}_keyword_tolerance`;
    }
    if (isInSolverConfig && keyName === 'relTol') {
      console.log(`Using specialized relTol keyword block for ${keyName} in solver config '${solverName}' at path: ${path}`);
      return `${PREFIX}_keyword_relTol`;
    }
    if (isInSolverConfig && keyName === 'nCellsInCoarsestLevel') {
      console.log(`Using specialized nCellsInCoarsestLevel keyword block for ${keyName} in solver config '${solverName}' at path: ${path}`);
      return `${PREFIX}_keyword_nCellsInCoarsestLevel`;
    }
    
    // Fallback for solver parameters when not in solver config (shouldn't happen but just in case)
    if (!isInSolverConfig && ['solver', 'smoother', 'tolerance', 'relTol', 'nCellsInCoarsestLevel'].includes(keyName)) {
      console.log(`Using specialized keyword block for ${keyName} outside solver config at path: ${path}`);
      return `${PREFIX}_keyword_${keyName}`;
    }
    
    // Relaxation factors - flexible path matching
    if (path.includes('relaxationFactors')) {
      return `${PREFIX}_relaxation_factor`;
    }
    
    // Fallback to common blocks
    return null;
  }

  return null;
}

// Export dictionary name and prefix for registry
export default {
  dictName: 'fvSolution',
  prefix: PREFIX,
  registerBlocks,
  generateToolbox,
  getBlocks: () => Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)),
  findMatchingBlockType: (component, dictName, path) => findMatchingBlockType(component, dictName, path),
  createBlock: createFvSolutionBlock,
  createKeywordBlock: createKeywordBlock
};

// Create fvSolution block
export function createFvSolutionBlock(workspace, entries = [], dictName = 'fvSolution') {
  try {
    const blockType = dictName === 'fvSolution' ? `${PREFIX}_dict` : 'common_dict';
    const block = createBlock(workspace, blockType);
    if (!block) {
      console.error(`Failed to create ${dictName} block`);
      return null;
    }

    const entriesInput = block.getInput('ENTRIES');
    if (entriesInput && entries.length > 0) {
      let prevBlock = null;
      
      for (const entry of entries) {
        const entryBlock = createBlockFromComponent(entry, workspace, dictName);
        if (entryBlock) {
          // Set values for key-value pairs - enhanced for new solver parameters
          if (entry.type === 'keyValue') {
            // Set the key field
            if (entryBlock.getField('KEY')) {
              entryBlock.setFieldValue(entry.key, 'KEY');
            }
            if (entryBlock.getField('VARIABLE')) {
              entryBlock.setFieldValue(entry.key, 'VARIABLE');
            }
            if (entryBlock.getField('SOLVER_NAME')) {
              entryBlock.setFieldValue(entry.key, 'SOLVER_NAME');
            }
            if (entryBlock.getField('ALGORITHM_NAME')) {
              entryBlock.setFieldValue(entry.key, 'ALGORITHM_NAME');
            }
          }
          
          // Handle dictionary entries (like solver configurations) - fix for solver name setting
          if (entry.type === 'dictionary') {
            const entryName = entry.key || entry.name;
            if (entryName) {
              if (entryBlock.getField('SOLVER_NAME')) {
                entryBlock.setFieldValue(entryName, 'SOLVER_NAME');
                console.log(`Set SOLVER_NAME field to: ${entryName} for dictionary entry`);
              }
              if (entryBlock.getField('ALGORITHM_NAME')) {
                entryBlock.setFieldValue(entryName, 'ALGORITHM_NAME');
              }
              if (entryBlock.getField('DICT_NAME')) {
                entryBlock.setFieldValue(entryName, 'DICT_NAME');
              }
            }
          }
          
          // Set value if it's a simple value - use enhanced logic for new parameters
          if (entry.type === 'keyValue' && typeof entry.value !== 'object' && entry.value !== null && entry.value !== undefined) {
            let valueBlock = null;
            
            // Handle specific solver parameter values
            switch (entry.key) {
              case 'solver':
                const solverOptions = SOLVER_OPTIONS.map(opt => opt[1]);
                if (solverOptions.includes(entry.value)) {
                  valueBlock = createBlock(workspace, `${PREFIX}_solver_type_dropdown`);
                  if (valueBlock && valueBlock.getField('SOLVER')) {
                    valueBlock.setFieldValue(entry.value, 'SOLVER');
                  }
                } else {
                  valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
                  if (valueBlock && valueBlock.getField('VALUE')) {
                    valueBlock.setFieldValue(String(entry.value), 'VALUE');
                  }
                }
                break;
                
              case 'smoother':
                const smootherOptions = SMOOTHER_OPTIONS.map(opt => opt[1]);
                if (smootherOptions.includes(entry.value)) {
                  valueBlock = createBlock(workspace, `${PREFIX}_smoother_dropdown`);
                  if (valueBlock && valueBlock.getField('SMOOTHER')) {
                    valueBlock.setFieldValue(entry.value, 'SMOOTHER');
                  }
                } else {
                  valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
                  if (valueBlock && valueBlock.getField('VALUE')) {
                    valueBlock.setFieldValue(String(entry.value), 'VALUE');
                  }
                }
                break;
                
              case 'tolerance':
              case 'relTol':
                if (typeof entry.value === 'number' || typeof entry.value === 'string') {
                  const numValue = typeof entry.value === 'string' ? parseFloat(entry.value) : entry.value;
                  
                  if (numValue < 0.001 && numValue > 0) {
                    const exponent = Math.floor(Math.log10(numValue));
                    const mantissa = numValue / Math.pow(10, exponent);
                    
                    valueBlock = createBlock(workspace, `${PREFIX}_value_scientific`);
                    if (valueBlock) {
                      if (valueBlock.getField('MANTISSA')) {
                        valueBlock.setFieldValue(Math.round(mantissa * 1000) / 1000, 'MANTISSA');
                      }
                      if (valueBlock.getField('EXPONENT')) {
                        valueBlock.setFieldValue(exponent, 'EXPONENT');
                      }
                    }
                  } else {
                    valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
                    if (valueBlock && valueBlock.getField('VALUE')) {
                      valueBlock.setFieldValue(numValue, 'VALUE');
                    }
                  }
                }
                break;
                
              case 'nCellsInCoarsestLevel':
                if (typeof entry.value === 'number' || typeof entry.value === 'string') {
                  const intValue = typeof entry.value === 'string' ? parseInt(entry.value, 10) : Math.round(entry.value);
                  valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
                  if (valueBlock && valueBlock.getField('VALUE')) {
                    valueBlock.setFieldValue(intValue, 'VALUE');
                  }
                }
                break;
                
              default:
                // Default handling - use existing logic but with enhanced type checking
                if (typeof entry.value === 'number') {
                  valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
                  if (valueBlock && valueBlock.getField('VALUE')) {
                    valueBlock.setFieldValue(entry.value, 'VALUE');
                  }
                } else {
                  valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
                  if (valueBlock && valueBlock.getField('VALUE')) {
                    valueBlock.setFieldValue(String(entry.value), 'VALUE');
                  }
                }
                break;
            }
            
            // Connect the value block
            if (valueBlock) {
              connectValueBlock(entryBlock, valueBlock, entry.key);
            }
          }

          // Connect blocks
          if (!prevBlock) {
            entriesInput.connection.connect(entryBlock.previousConnection);
          } else if (prevBlock.nextConnection && entryBlock.previousConnection) {
            prevBlock.nextConnection.connect(entryBlock.previousConnection);
          }
          prevBlock = entryBlock;
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating block for ${dictName}:`, error);
    return null;
  }
}

// Create keyword block
export function createKeywordBlock(workspace, key, value) {
  try {
    console.log(`Creating keyword block for ${key}:`, value);
    
    const blockType = findMatchingBlockType({ type: 'keyValue', key: key }, 'fvSolution');
    if (!blockType) {
      console.error(`No block type found for keyword: ${key}`);
      return null;
    }

    const block = createBlock(workspace, blockType);
    if (!block) {
      console.error(`Failed to create block for keyword: ${key}`);
      return null;
    }

    // Set field values based on block type - enhanced for specialized keyword blocks
    if (block.getField('KEY')) {
      block.setFieldValue(key, 'KEY');
    }
    if (block.getField('VARIABLE')) {
      block.setFieldValue(key, 'VARIABLE');
    }
    if (block.getField('SOLVER_NAME')) {
      block.setFieldValue(key, 'SOLVER_NAME');
    }
    if (block.getField('ALGORITHM_NAME')) {
      block.setFieldValue(key, 'ALGORITHM_NAME');
    }

    // Create and connect value block if needed - enhanced for specialized blocks
    if (typeof value !== 'object' && value !== null && value !== undefined) {
      let valueBlock = null;
      
      // For specialized keyword blocks, use the appropriate value block type
      if (blockType.startsWith(`${PREFIX}_keyword_`)) {
        // Handle specialized keyword blocks based on the key
        switch (key) {
          case 'solver':
            const solverOptions = SOLVER_OPTIONS.map(opt => opt[1]);
            if (solverOptions.includes(value)) {
              valueBlock = createBlock(workspace, `${PREFIX}_solver_type_dropdown`);
              if (valueBlock && valueBlock.getField('SOLVER')) {
                valueBlock.setFieldValue(value, 'SOLVER');
              }
            } else {
              valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
              if (valueBlock && valueBlock.getField('VALUE')) {
                valueBlock.setFieldValue(String(value), 'VALUE');
              }
            }
            break;
            
          case 'smoother':
            const smootherOptions = SMOOTHER_OPTIONS.map(opt => opt[1]);
            if (smootherOptions.includes(value)) {
              valueBlock = createBlock(workspace, `${PREFIX}_smoother_dropdown`);
              if (valueBlock && valueBlock.getField('SMOOTHER')) {
                valueBlock.setFieldValue(value, 'SMOOTHER');
              }
            } else {
              valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
              if (valueBlock && valueBlock.getField('VALUE')) {
                valueBlock.setFieldValue(String(value), 'VALUE');
              }
            }
            break;
            
          case 'tolerance':
          case 'relTol':
            if (typeof value === 'number' || typeof value === 'string') {
              const numValue = typeof value === 'string' ? parseFloat(value) : value;
              
              if (numValue < 0.001 && numValue > 0) {
                const exponent = Math.floor(Math.log10(numValue));
                const mantissa = numValue / Math.pow(10, exponent);
                
                valueBlock = createBlock(workspace, `${PREFIX}_value_scientific`);
                if (valueBlock) {
                  if (valueBlock.getField('MANTISSA')) {
                    valueBlock.setFieldValue(Math.round(mantissa * 1000) / 1000, 'MANTISSA');
                  }
                  if (valueBlock.getField('EXPONENT')) {
                    valueBlock.setFieldValue(exponent, 'EXPONENT');
                  }
                }
              } else {
                valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
                if (valueBlock && valueBlock.getField('VALUE')) {
                  valueBlock.setFieldValue(numValue, 'VALUE');
                }
              }
            }
            break;
            
          case 'nCellsInCoarsestLevel':
            if (typeof value === 'number' || typeof value === 'string') {
              const intValue = typeof value === 'string' ? parseInt(value, 10) : Math.round(value);
              valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
              if (valueBlock && valueBlock.getField('VALUE')) {
                valueBlock.setFieldValue(intValue, 'VALUE');
              }
            }
            break;
            
          default:
            // Default handling for specialized blocks
            if (typeof value === 'number') {
              valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
              if (valueBlock && valueBlock.getField('VALUE')) {
                valueBlock.setFieldValue(value, 'VALUE');
              }
            } else {
              valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
              if (valueBlock && valueBlock.getField('VALUE')) {
                valueBlock.setFieldValue(String(value), 'VALUE');
              }
            }
            break;
        }
      } else {
        // Original logic for non-specialized blocks
        if (typeof value === 'number') {
          valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
          if (valueBlock && valueBlock.getField('VALUE')) {
            valueBlock.setFieldValue(value, 'VALUE');
          }
        } else {
          valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
          if (valueBlock && valueBlock.getField('VALUE')) {
            valueBlock.setFieldValue(String(value), 'VALUE');
          }
        }
      }
      
      // Connect the value block
      if (valueBlock) {
        const connected = connectValueBlock(block, valueBlock, key);
        if (!connected) {
          console.warn(`Failed to connect value block for ${key}`);
        }
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating keyword block for ${key}:`, error);
    return null;
  }
}

// Helper function to create blocks from components
function createBlockFromComponent(component, workspace, dictName) {
  const blockType = findMatchingBlockType(component, dictName);
  if (!blockType) {
    return null; // Fall back to common blocks
  }

  const block = createBlock(workspace, blockType);
  if (!block) {
    return null;
  }

  // Set key field if it exists - fix for solver configuration dictionaries
  const keyValue = component.key || component.name;
  if (keyValue) {
    // Set appropriate field based on block type
    if (block.getField('KEY')) {
      block.setFieldValue(keyValue, 'KEY');
    }
    if (block.getField('VARIABLE')) {
      block.setFieldValue(keyValue, 'VARIABLE');
    }
    if (block.getField('SOLVER_NAME')) {
      block.setFieldValue(keyValue, 'SOLVER_NAME');
      console.log(`Set SOLVER_NAME field to: ${keyValue} for block type: ${blockType}`);
    }
    if (block.getField('ALGORITHM_NAME')) {
      block.setFieldValue(keyValue, 'ALGORITHM_NAME');
    }
  }

  // Handle value setting for keyValue components
  if (component.type === 'keyValue' && component.value !== undefined) {
    const value = component.value;
    
    // If the value is not an object, create and connect a value block
    if (typeof value !== 'object' && value !== null) {
      let valueBlock = null;
      
      // Use the same logic as createKeywordBlock for consistency
      switch (keyValue) {
        case 'solver':
          const solverOptions = SOLVER_OPTIONS.map(opt => opt[1]);
          if (solverOptions.includes(value)) {
            valueBlock = createBlock(workspace, `${PREFIX}_solver_type_dropdown`);
            if (valueBlock && valueBlock.getField('SOLVER')) {
              valueBlock.setFieldValue(value, 'SOLVER');
            }
          } else {
            valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
            if (valueBlock && valueBlock.getField('VALUE')) {
              valueBlock.setFieldValue(String(value), 'VALUE');
            }
          }
          break;
          
        case 'smoother':
          const smootherOptions = SMOOTHER_OPTIONS.map(opt => opt[1]);
          if (smootherOptions.includes(value)) {
            valueBlock = createBlock(workspace, `${PREFIX}_smoother_dropdown`);
            if (valueBlock && valueBlock.getField('SMOOTHER')) {
              valueBlock.setFieldValue(value, 'SMOOTHER');
            }
          } else {
            valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
            if (valueBlock && valueBlock.getField('VALUE')) {
              valueBlock.setFieldValue(String(value), 'VALUE');
            }
          }
          break;
          
        case 'tolerance':
        case 'relTol':
          if (typeof value === 'number' || typeof value === 'string') {
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            
            if (numValue < 0.001 && numValue > 0) {
              const exponent = Math.floor(Math.log10(numValue));
              const mantissa = numValue / Math.pow(10, exponent);
              
              valueBlock = createBlock(workspace, `${PREFIX}_value_scientific`);
              if (valueBlock) {
                if (valueBlock.getField('MANTISSA')) {
                  valueBlock.setFieldValue(Math.round(mantissa * 1000) / 1000, 'MANTISSA');
                }
                if (valueBlock.getField('EXPONENT')) {
                  valueBlock.setFieldValue(exponent, 'EXPONENT');
                }
              }
            } else {
              valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
              if (valueBlock && valueBlock.getField('VALUE')) {
                valueBlock.setFieldValue(numValue, 'VALUE');
              }
            }
          }
          break;
          
        case 'nCellsInCoarsestLevel':
          if (typeof value === 'number' || typeof value === 'string') {
            const intValue = typeof value === 'string' ? parseInt(value, 10) : Math.round(value);
            valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
            if (valueBlock && valueBlock.getField('VALUE')) {
              valueBlock.setFieldValue(intValue, 'VALUE');
            }
          }
          break;
          
        default:
          // Default value handling
          if (typeof value === 'number') {
            valueBlock = createBlock(workspace, `${PREFIX}_value_number`);
            if (valueBlock && valueBlock.getField('VALUE')) {
              valueBlock.setFieldValue(value, 'VALUE');
            }
          } else {
            valueBlock = createBlock(workspace, `${PREFIX}_value_string`);
            if (valueBlock && valueBlock.getField('VALUE')) {
              valueBlock.setFieldValue(String(value), 'VALUE');
            }
          }
          break;
      }
      
      // Connect the value block
      if (valueBlock) {
        connectValueBlock(block, valueBlock, keyValue);
      }
    }
  }

  return block;
}